import { LinkButton } from '@timelyapp/react';
import Badge from 'components/Badge';
import { Brain } from 'components/Icons';
import Spinner from 'components/Spinner';
import type { MemoryEmptyStates } from 'factories/timeline';
import React, { Suspense } from 'react';
import FontAwesome from 'react-fontawesome';
import styles from './MemoryEmptyStates.module.scss';

const MemorySidebarNoSources = React.lazy(() => import('./MemorySidebarNoSources'));

interface Props {
  emptyState: MemoryEmptyStates;
  isToday: boolean;
}

export function MemoryEmptyState({ emptyState, isToday }: Props) {
  // Memoize this so spinner "reset" on re-render
  const renderStates = React.useMemo(() => {
    switch (emptyState) {
      case 'loading-memories':
        return (
          <div className={styles.icon} style={{ top: -12, left: -1 }}>
            <Brain />
            <Spinner
              className={styles.spinner}
              radius={48}
              lines={24}
              width={5}
              speed={1 / 3}
              animation="spinner-line-fade-more"
              color="currentColor"
            />
          </div>
        );
      case 'no-memory-apps':
        return (
          <Suspense fallback={<Spinner />}>
            <MemorySidebarNoSources />
          </Suspense>
        );
      case 'no-memories-on-day':
        return (
          <React.Fragment>
            <div className={styles.icon}>
              <Brain />
              {isToday && (
                <Spinner
                  className={styles.spinner}
                  radius={48}
                  lines={24}
                  width={5}
                  speed={1 / 3}
                  animation="spinner-line-fade-more"
                  color="currentColor"
                />
              )}
            </div>
            <p>No memories {isToday ? 'yet' : 'found'}</p>
            <LinkButton
              size="small"
              linkTo="https://timelyapp.com/features/automatic-time-tracking"
              newWindow
            >
              Learn more about automatic time tracking
            </LinkButton>
          </React.Fragment>
        );
      case 'no-memories-on-day-in-future':
        return (
          <React.Fragment>
            <div className={styles.icon}>
              <Brain />
            </div>
            <p>The future is looking clear!</p>
            <p>Nothing has happened on this day yet</p>
          </React.Fragment>
        );
      case 'inbox-zero-with-filters':
        return (
          <React.Fragment>
            <div className={styles.icon}>
              <Badge variant="success" size="small" position="topRight">
                <FontAwesome name="question" />
              </Badge>
              <Brain />
            </div>
            <p>No memories match current filters</p>
            <p>Adjust you filters to see relevant memories</p>
          </React.Fragment>
        );
      case 'inbox-zero':
        return (
          <React.Fragment>
            <div className={styles.icon}>
              <Badge variant="success" size="small" position="topRight">
                <FontAwesome name="check" />
              </Badge>
              <Brain />
            </div>
            <p>All memories logged, good job!</p>
          </React.Fragment>
        );
      default:
        return (
          <React.Fragment>@todo define missing emptyType, defined as {emptyState}!</React.Fragment>
        );
    }
  }, [emptyState, isToday]);

  return <div className={styles.container}>{renderStates}</div>;
}
